import { Injectable, inject } from '@angular/core';
import { Observable, map, catchError, throwError, of, switchMap } from 'rxjs';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import {
  VirtualWarehouse,
  VirtualWarehousePageResponse,
  VirtualWarehouseSearchParams,
  VirtualWarehouseCreateRequest,
  VirtualWarehouseUpdateRequest,
  VirtualWarehouseApiCreateRequest,
  PhysicalWarehouseCandidate,
} from '../models/virtual-warehouse.model';
import {
  WarehouseControllerService,
  WarehouseQueryReqDTO,
  PageInfoBranchWarehouseListVO,
  PageInfoWarehouseQueryResVO,
  BranchWarehouseListVO,
  BranchWarehouseDetailVO,
  BranchWarehouseCreateDTO,
  BranchWarehouseUpdateDTO,
  VirtualWarehouseCreateDTO,
  VirtualWarehouseDetailVO,
  VirtualWarehouseUpdateDTO,
  WarehouseQueryResVO,
  WarehouseSuggestionVO,
} from '@app/api/stock-service';

@Injectable({
  providedIn: 'root',
})
export class VirtualWarehouseService {
  private apiService = inject(WarehouseControllerService);

  /**
   * Get virtual warehouses with pagination
   */
  getVirtualWarehouses(
    params: VirtualWarehouseSearchParams,
  ): Observable<VirtualWarehousePageResponse> {
    const queryDTO: WarehouseQueryReqDTO & { cityCode?: number } = {
      page: params.page,
      size: params.size,
      name: params.name?.trim() || undefined,
      status: params.status,
      cityCode: params.cityCode,
      sortBy: 'creationDate',
      ascending: false,
    };

    // Remove undefined values to avoid sending empty strings
    if (!queryDTO.name) {
      delete queryDTO.name;
    }
    if (queryDTO.cityCode === undefined || queryDTO.cityCode === null) {
      delete queryDTO.cityCode;
    }
    if (queryDTO.status === undefined) {
      delete queryDTO.status;
    }

    return this.apiService.queryBranchWarehousePage(queryDTO).pipe(
      switchMap(
        (
          response:
            | PageInfoBranchWarehouseListVO
            | HttpResponse<PageInfoBranchWarehouseListVO>
            | Blob,
        ) => {
          if (response instanceof Blob) {
            return new Observable<PageInfoBranchWarehouseListVO>((observer) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result as string);
                  observer.next(json);
                  observer.complete();
                } catch (error) {
                  console.error('Error parsing Blob to JSON:', error);
                  observer.error(error);
                }
              };
              reader.onerror = (error) => {
                console.error('Error reading Blob:', error);
                observer.error(error);
              };
              reader.readAsText(response);
            });
          }
          if (response instanceof HttpResponse) {
            return of(response.body!);
          }
          return of(response as PageInfoBranchWarehouseListVO);
        },
      ),
      map((pageInfo: PageInfoBranchWarehouseListVO) => {
        const warehouses: VirtualWarehouse[] =
          (pageInfo.pageContent || []).map((item: BranchWarehouseListVO) =>
            this.mapBranchWarehouseListVOToVirtualWarehouse(item),
          ) || [];

        return {
          content: warehouses,
          totalElements: pageInfo.totalElements || 0,
          totalPages: pageInfo.totalPages || 0,
          size: pageInfo.size || params.size,
          number: pageInfo.number || params.page,
          first: (pageInfo.number || 0) === 0,
          last: (pageInfo.number || 0) >= (pageInfo.totalPages || 1) - 1,
          numberOfElements: pageInfo.numberOfElements || 0,
        };
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading virtual warehouses:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Get virtual warehouses from POST /v1/warehouses/virtual/page.
   * Only adds name, code, typeCodes, cityName, cityCode, ids to payload when provided.
   */
  getVirtualWarehousesFromVirtualPage(params: {
    page?: number;
    size?: number;
    name?: string;
    code?: string[];
    typeCodes?: string[];
    cityName?: string;
    cityCode?: number;
    ids?: number[];
  }): Observable<VirtualWarehousePageResponse> {
    const searchName =
      params.name?.trim() && params.name.trim().length > 0
        ? params.name.trim()
        : undefined;
    let searchCodes: string[] | undefined;
    if (params.code && params.code.length > 0) {
      const trimmed = params.code
        .map((c) => c?.trim())
        .filter((c) => c && c.length > 0);
      searchCodes = trimmed.length > 0 ? trimmed : undefined;
    }
    const searchTypeCodes =
      params.typeCodes?.length && params.typeCodes.length > 0
        ? params.typeCodes.filter((t) => t?.trim())
        : undefined;
    const searchCityName =
      params.cityName?.trim() && params.cityName.trim().length > 0
        ? params.cityName.trim()
        : undefined;
    const searchCityCode =
      params.cityCode != null && !Number.isNaN(Number(params.cityCode))
        ? Number(params.cityCode)
        : undefined;
    const searchIds =
      params.ids?.length && params.ids.length > 0 ? params.ids : undefined;

    const queryDTO: WarehouseQueryReqDTO = {
      page: params.page ?? 0,
      size: params.size ?? 10,
      sortBy: 'id',
      ascending: true,
      typeCodes: searchTypeCodes ?? [],
      ...(searchName != null && { name: searchName }),
      ...(searchCodes != null && { code: searchCodes }),
      ...(searchCityName != null && { cityName: searchCityName }),
      ...(searchCityCode != null && { cityCode: searchCityCode }),
      ...(searchIds != null && { ids: searchIds }),
    };

    return this.apiService.queryVirtualWarehousePage(queryDTO).pipe(
      switchMap(
        (
          response:
            | PageInfoBranchWarehouseListVO
            | HttpResponse<PageInfoBranchWarehouseListVO>
            | Blob,
        ) => {
          if (response instanceof Blob) {
            return new Observable<PageInfoBranchWarehouseListVO>((observer) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result as string);
                  observer.next(json);
                  observer.complete();
                } catch (error) {
                  console.error('Error parsing Blob to JSON:', error);
                  observer.error(error);
                }
              };
              reader.onerror = (error) => {
                console.error('Error reading Blob:', error);
                observer.error(error);
              };
              reader.readAsText(response);
            });
          }
          if (response instanceof HttpResponse) {
            return of(response.body!);
          }
          return of(response as PageInfoBranchWarehouseListVO);
        },
      ),
      map((pageInfo: PageInfoBranchWarehouseListVO) => {
        const warehouses: VirtualWarehouse[] =
          (pageInfo.pageContent || []).map((item: BranchWarehouseListVO) =>
            this.mapBranchWarehouseListVOToVirtualWarehouse(item),
          ) || [];
        const page = params.page ?? 0;
        const size = params.size ?? 10;
        return {
          content: warehouses,
          totalElements: pageInfo.totalElements || 0,
          totalPages: pageInfo.totalPages || 0,
          size: pageInfo.size ?? size,
          number: pageInfo.number ?? page,
          first: (pageInfo.number ?? 0) === 0,
          last:
            (pageInfo.number ?? 0) >= (pageInfo.totalPages || 1) - 1,
          numberOfElements: pageInfo.numberOfElements || 0,
        };
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading virtual warehouses (virtual/page):', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Get virtual warehouse by ID
   */
  getVirtualWarehouseById(id: number): Observable<VirtualWarehouse> {
    return this.apiService.getBranchWarehouseDetail(id).pipe(
      switchMap((response: any) => {
        if (response instanceof Blob) {
          return new Observable<any>((observer) => {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const json = JSON.parse(reader.result as string);
                observer.next(json);
                observer.complete();
              } catch (error) {
                console.error('Error parsing Blob to JSON:', error);
                observer.error(error);
              }
            };
            reader.onerror = (error) => {
              console.error('Error reading Blob:', error);
              observer.error(error);
            };
            reader.readAsText(response);
          });
        }
        if (response instanceof HttpResponse) {
          return of(response.body!);
        }
        return of(response);
      }),
      map((warehouse: BranchWarehouseDetailVO) =>
        this.mapBranchWarehouseDetailVOToVirtualWarehouse(warehouse),
      ),
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading virtual warehouse:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Get member warehouses for a branch warehouse (detail endpoint)
   */
  getBranchWarehouseMembers(
    id: number,
  ): Observable<PhysicalWarehouseCandidate[]> {
    return this.apiService.getBranchWarehouseDetail(id).pipe(
      map((dto: BranchWarehouseDetailVO) => {
        return (
          dto.members?.map((m) => ({
            id: m.id || 0,
            name: m.name || '',
            code: m.code || '',
            typeCode: m.typeCode || '',
            typeName: (m as any).typeName || m.typeCode || '',
            contactName: (m as any).contactName || (m as any).linkman || '',
            contactPhone:
              (m as any).contactPhone ||
              (m as any).phone ||
              (m as any).mobile ||
              '',
            address: (m as any).address || (m as any).detailAddress || '',
            cityName: m.cityName,
          })) || []
        );
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading branch warehouse members:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Get physical warehouse candidates by city codes
   */
  getPhysicalWarehouseCandidates(
    cityCodes: string | string[],
  ): Observable<PhysicalWarehouseCandidate[]> {
    const codesArray = Array.isArray(cityCodes) ? cityCodes : [cityCodes];
    return this.apiService.getPhysicalWarehouseCandidates(codesArray).pipe(
      switchMap((response: any) => {
        if (response instanceof Blob) {
          return new Observable<any>((observer) => {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const json = JSON.parse(reader.result as string);
                observer.next(json);
                observer.complete();
              } catch (error) {
                console.error('Error parsing Blob to JSON:', error);
                observer.error(error);
              }
            };
            reader.onerror = (error) => {
              console.error('Error reading Blob:', error);
              observer.error(error);
            };
            reader.readAsText(response);
          });
        }
        if (response instanceof HttpResponse) {
          return of(response.body!);
        }
        return of(response);
      }),
      map((candidates: WarehouseSuggestionVO[]) =>
        candidates.map((c: any) => ({
          id: c.id!,
          name: c.name || '',
          code: c.code || '',
          typeCode: c.typeCode || '',
          typeName: c.typeName || '',
          contactName: c.contactName || c.linkman || '',
          contactPhone: c.contactPhone || c.phone || c.mobile || '',
          address: c.address || '',
          cityName: c.cityName,
        })),
      ),
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading physical warehouse candidates:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Create branch warehouse (existing flow, used by virtual-warehouse-form).
   */
  createVirtualWarehouse(
    warehouse: VirtualWarehouseCreateRequest,
  ): Observable<string> {
    const createDTO: BranchWarehouseCreateDTO = {
      tenantId: warehouse.tenantId,
      name: warehouse.name,
      code: warehouse.code,
      status: warehouse.status ?? true,
      memberWarehouseIds: warehouse.memberWarehouseIds,
    };

    return this.apiService
      .createBranchWarehouse(createDTO, 'body', false, {
        httpHeaderAccept: 'text/plain' as '*/*',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const errorJson = JSON.parse(error.error ?? '{}');
          return throwError(() => new HttpErrorResponse({ error: errorJson }));
        }),
      );
  }

  /**
   * Update branch warehouse (existing flow).
   */
  updateVirtualWarehouse(
    id: number,
    warehouse: VirtualWarehouseUpdateRequest,
  ): Observable<string> {
    const updateDTO: BranchWarehouseUpdateDTO = {
      name: warehouse.name,
      status: warehouse.status,
      memberWarehouseIds: warehouse.memberWarehouseIds,
    };

    return this.apiService
      .updateBranchWarehouse(id, updateDTO, 'body', false, {
        httpHeaderAccept: 'text/plain' as '*/*',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const errorJson = JSON.parse(error.error ?? '{}');
          return throwError(() => new HttpErrorResponse({ error: errorJson }));
        }),
      );
  }

  /**
   * Get virtual warehouse detail via GET /v1/warehouses/virtual/{id}.
   */
  getVirtualWarehouseDetailApi(id: number): Observable<VirtualWarehouseDetailVO> {
    return this.apiService.getVirtualWarehouseDetail(id).pipe(
      switchMap(
        (
          response:
            | VirtualWarehouseDetailVO
            | HttpResponse<VirtualWarehouseDetailVO>
            | Blob,
        ) => {
          if (response instanceof Blob) {
            return new Observable<VirtualWarehouseDetailVO>((observer) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result as string);
                  observer.next(json);
                  observer.complete();
                } catch (e) {
                  observer.error(e);
                }
              };
              reader.onerror = (e) => observer.error(e);
              reader.readAsText(response);
            });
          }
          if (response instanceof HttpResponse) {
            return of(response.body!);
          }
          return of(response as VirtualWarehouseDetailVO);
        },
      ),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  /**
   * Update virtual warehouse via PUT /v1/warehouses/virtual/{id}.
   */
  updateVirtualWarehouseApi(
    id: number,
    payload: {
      name: string;
      status: boolean;
      memberWarehouseIds: number[];
      updatedBy?: string;
    },
  ): Observable<string> {
    const dto: VirtualWarehouseUpdateDTO = {
      name: payload.name,
      status: payload.status,
      memberWarehouseIds: payload.memberWarehouseIds,
      updatedBy: payload.updatedBy,
    };
    return this.apiService.updateVirtualWarehouse(id, dto).pipe(
      catchError((error: HttpErrorResponse) => {
        const err =
          typeof error.error === 'string'
            ? error.error
            : JSON.stringify(error.error ?? {});
        try {
          return throwError(
            () => new HttpErrorResponse({ error: JSON.parse(err) }),
          );
        } catch {
          return throwError(() => error);
        }
      }),
    );
  }

  /**
   * Create virtual warehouse via POST /v1/warehouses/virtual (new Kho ảo API).
   */
  createVirtualWarehouseApi(
    request: VirtualWarehouseApiCreateRequest,
  ): Observable<number> {
    const dto: VirtualWarehouseCreateDTO = {
      typeCode: request.typeCode,
      name: request.name,
      code: request.code ?? '',
      status: request.status ?? true,
      memberWarehouseIds:
        request.memberWarehouseIds == null ||
        request.memberWarehouseIds.length === 0
          ? undefined
          : request.memberWarehouseIds,
      createdBy: request.createdBy,
    };
    return this.apiService.createVirtualWarehouse(dto).pipe(
      catchError((error: HttpErrorResponse) => {
        const err =
          typeof error.error === 'string'
            ? error.error
            : JSON.stringify(error.error ?? {});
        try {
          return throwError(
            () => new HttpErrorResponse({ error: JSON.parse(err) }),
          );
        } catch {
          return throwError(() => error);
        }
      }),
    );
  }

  /**
   * Load logical warehouses by type for member selection (Tên kho hàng).
   * Calls POST /v1/warehouses/logical-warehouse. Used by Kho ảo create form.
   */
  getLogicalWarehousesByType(
    typeCodes: string[],
    params?: { page?: number; size?: number },
  ): Observable<Array<{ id: number; name: string; code: string }>> {
    if (!typeCodes?.length) {
      return of([]);
    }
    const queryDTO: WarehouseQueryReqDTO = {
      page: params?.page ?? 0,
      size: params?.size ?? 500,
      sortBy: 'name',
      ascending: true,
      typeCodes,
      status: true,
    };

    return this.apiService.queryLogicalWarehouse(queryDTO).pipe(
      switchMap(
        (
          response:
            | PageInfoWarehouseQueryResVO
            | HttpResponse<PageInfoWarehouseQueryResVO>
            | Blob,
        ) => {
          if (response instanceof Blob) {
            return new Observable<PageInfoWarehouseQueryResVO>((observer) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result as string);
                  observer.next(json);
                  observer.complete();
                } catch (e) {
                  observer.error(e);
                }
              };
              reader.onerror = (e) => observer.error(e);
              reader.readAsText(response);
            });
          }
          if (response instanceof HttpResponse) {
            return of(response.body!);
          }
          return of(response as PageInfoWarehouseQueryResVO);
        },
      ),
      map((pageInfo: PageInfoWarehouseQueryResVO) => {
        const list = pageInfo.pageContent || [];
        return list
          .filter((item: WarehouseQueryResVO) => item.id != null)
          .map((item: WarehouseQueryResVO) => ({
            id: item.id!,
            name: item.name || '',
            code: item.code || '',
          }));
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading logical warehouses by type:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Load physical (branch) warehouses by type for member selection.
   * Used when branch warehouse list is needed.
   */
  getBranchWarehousesByType(
    typeCodes: string[],
    params?: { page?: number; size?: number },
  ): Observable<PhysicalWarehouseCandidate[]> {
    if (!typeCodes?.length) {
      return of([]);
    }
    const queryDTO: WarehouseQueryReqDTO = {
      page: params?.page ?? 0,
      size: params?.size ?? 500,
      sortBy: 'name',
      ascending: true,
      typeCodes,
      status: true,
    };

    return this.apiService.queryBranchWarehousePage(queryDTO).pipe(
      switchMap(
        (
          response:
            | PageInfoBranchWarehouseListVO
            | HttpResponse<PageInfoBranchWarehouseListVO>
            | Blob,
        ) => {
          if (response instanceof Blob) {
            return new Observable<PageInfoBranchWarehouseListVO>((observer) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result as string);
                  observer.next(json);
                  observer.complete();
                } catch (e) {
                  observer.error(e);
                }
              };
              reader.onerror = (e) => observer.error(e);
              reader.readAsText(response);
            });
          }
          if (response instanceof HttpResponse) {
            return of(response.body!);
          }
          return of(response as PageInfoBranchWarehouseListVO);
        },
      ),
      map((pageInfo: PageInfoBranchWarehouseListVO) => {
        const list = pageInfo.pageContent || [];
        return list.map((item: BranchWarehouseListVO) => ({
          id: item.id!,
          name: item.name || '',
          code: item.code || '',
          typeCode: (item as any).typeCode || '',
          typeName: (item as any).typeName,
          contactName: (item as any).contactName,
          contactPhone: (item as any).contactPhone,
          address: (item as any).address,
          cityName: (item as any).cityName,
        }));
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading branch warehouses by type:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Map BranchWarehouseListVO to VirtualWarehouse
   */
  private mapBranchWarehouseListVOToVirtualWarehouse(
    item: BranchWarehouseListVO,
  ): VirtualWarehouse {
    const cityNamesJoined = item.cities?.map((c) => c.cityName).join(', ');
    return {
      id: item.id,
      name: item.name || '',
      code: item.code || '',
      typeCode: item.typeCode,
      status: item.status ?? true,
      cityName: cityNamesJoined,
      cityCode: item.cities?.[0]?.cityCode?.toString(),
      memberWarehouseIds: [],
      memberWarehouseNames: item.memberWarehouseNames || [],
      creationDate: item.creationDate,
      lastUpdateDate: item.lastUpdateDate,
    };
  }

  /**
   * Map BranchWarehouseDetailVO to VirtualWarehouse
   */
  private mapBranchWarehouseDetailVOToVirtualWarehouse(
    dto: BranchWarehouseDetailVO,
  ): VirtualWarehouse {
    // Get cityName and cityCode from first member if available
    const firstMember =
      dto.members && dto.members.length > 0 ? dto.members[0] : null;
    const cityName = firstMember?.cityName;
    const cityCode = firstMember?.cityCode?.toString();
    const memberWarehouseCityCodes = dto.members?.map(
      (m) => m.cityCode,
    ) as number[];

    // Map member warehouse names for display
    const memberWarehouseNames =
      dto.members?.map((m) => {
        if (m.name && m.code) {
          return `${m.name} (${m.code})`;
        }
        return m.name || m.code || '';
      }) || [];

    const memberWarehouseDetails =
      dto.members?.map((m) => ({
        id: m.id || 0,
        code: m.code || '',
        name: m.name || '',
        typeCode: m.typeCode || '',
        typeName: (m as any).typeName || m.typeCode || '',
        contactName: (m as any).contactName || (m as any).linkman || '',
        contactPhone:
          (m as any).contactPhone ||
          (m as any).phone ||
          (m as any).mobile ||
          '',
        address: (m as any).address || (m as any).detailAddress || '',
      })) || [];

    return {
      id: dto.id,
      name: dto.name || '',
      code: dto.code || '',
      status: dto.status ?? true,
      tenantId: dto.tenantId,
      cityName: cityName,
      cityCode: cityCode,
      memberWarehouseCityCodes,
      memberWarehouseIds: memberWarehouseDetails
        .map((m) => m.id)
        .filter((id) => id > 0),
      memberWarehouseNames: memberWarehouseNames,
      memberWarehouses: memberWarehouseDetails,
      creationDate: dto.creationDate,
      lastUpdateDate: dto.lastUpdateDate,
    };
  }

  /**
   * Delete virtual warehouses by IDs
   * @param ids Array of warehouse IDs to delete
   */
  deleteVirtualWarehouses(ids: number[]): Observable<void> {
    if (!ids || ids.length === 0) {
      return throwError(() => new Error('No warehouse IDs provided'));
    }

    return this.apiService
      .deleteWarehouse(ids, 'body', false, {
        httpHeaderAccept: '*/*',
      })
      .pipe(
        map(() => {
          // API returns void on success
          return;
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('Error deleting virtual warehouses:', error);
          return throwError(() => error);
        }),
      );
  }
}
